import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { SectionEyebrow } from "./SectionEyebrow";
import { buildLocalizedPath } from "../lib/langRoutes";

const HERO_IMG = "/hero-before-after.jpg";

export const Hero = () => {
  const { t, i18n } = useTranslation();
  const { ref, inView } = useScrollReveal();
  const lang = i18n.language?.split("-")[0] || "de";

  return (
    <section id="top" data-testid="hero-section" className="relative pt-36 lg:pt-44 pb-20 lg:pb-28 overflow-hidden bg-[#F6F5F2]">
      <div className="absolute top-24 -left-20 w-96 h-96 rounded-full bg-[#A7CA99]/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div ref={ref} className={`lg:col-span-6 reveal ${inView ? "in-view" : ""}`}>
          <div data-testid="hero-eyebrow">
            <SectionEyebrow num="01">{t("hero.eyebrow")}</SectionEyebrow>
          </div>
          <h1 data-testid="hero-headline" className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[80px] font-light tracking-tight leading-[0.95] text-[#3E5239]">
            {t("hero.title_l1")}
            <br />
            <span className="italic text-[#A7CA99]">{t("hero.title_l2")}</span>
            <br />
            {t("hero.title_l3")}
          </h1>
          <p data-testid="hero-subheadline" className="mt-8 max-w-xl text-base lg:text-lg font-light text-[#6B7A66] leading-relaxed">
            {t("hero.subtitle")}
          </p>

          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <a href="#contato-rapido" data-testid="hero-cta-primary" className="group inline-flex items-center gap-3 px-8 py-4 bg-[#3E5239] text-[#F6F5F2] text-xs uppercase tracking-[0.2em] hover:bg-[#A7CA99] hover:text-[#3E5239] transition-all">
              {t("hero.cta")}
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </a>
            <Link to={buildLocalizedPath("treatments", lang)} data-testid="hero-cta-secondary" className="text-xs uppercase tracking-[0.2em] text-[#3E5239] hover:text-[#A7CA99] border-b border-[#3E5239]/30 hover:border-[#A7CA99] pb-1 transition-colors">
              {t("hero.cta_secondary")}
            </Link>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div className="relative overflow-hidden bg-[#EFEDE6] aspect-[4/3] shadow-[0_20px_60px_-20px_rgba(62,82,57,0.35)]">
            <img
              src={HERO_IMG}
              alt="Resultado real de tratamento estético — comparação antes/depois na KosMedico Zürich"
              data-testid="hero-image"
              className="w-full h-full object-cover object-center transition-transform duration-[1200ms] hover:scale-[1.03]"
            />

            {/* Subtle divider hint between halves */}
            <div className="absolute top-0 bottom-0 left-1/2 w-px bg-[#F6F5F2]/30 pointer-events-none" />

            {/* Per-half labels at top */}
            <div className="absolute top-4 left-4 px-3 py-1 bg-[#1F2D1D]/70 backdrop-blur-sm text-[#F6F5F2] text-[10px] uppercase tracking-[0.22em]">
              {t("hero.before")}
            </div>
            <div className="absolute top-4 right-4 px-3 py-1 bg-[#A7CA99] text-[#1F2D1D] text-[10px] uppercase tracking-[0.22em] font-semibold">
              {t("hero.after")}
            </div>
          </div>
          <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#A7CA99] -z-10 hidden lg:block" />
        </div>
      </div>
    </section>
  );
};
